import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { WebSocketService } from 'src/@core/services/websocket.service';
import { API_WEB_SOCKET } from 'src/@core/services/util/api.endpoints';

@Component({
  selector: 'hour4u-admin-live-transcription',
  templateUrl: './live-transcription.component.html',
  styleUrls: ['./live-transcription.component.scss']
})
export class LiveTranscriptionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() jobId: string | null = null;

  @ViewChild('scrollContainer', { static: false }) scrollContainer: ElementRef;

  transcript: Array<{
    jobId: string;
    role: 'user' | 'assistant' | string;
    text: string;
    ts?: number;
    is_final?: boolean;
    seq?: number;
  }> = [];

  isSubscribed = false;
  lastSeqSeen: number | null = null;
  isAutoScrollPaused = false;
  userInitial = 'U';

  private destroyed$ = new Subject<void>();

  constructor(private wsService: WebSocketService) { }

  ngOnInit() {
    this.wsService.ensureConnected(API_WEB_SOCKET);
    this.wsService.messages$
      .pipe(
        takeUntil(this.destroyed$),
        filter((msg) => !!msg)
      )
      .subscribe((msg: any) => {
        // server sends transcript rows as { type: "transcript_delta", jobId, ... }
        if (msg && msg.type === 'transcript_delta') {
          if (!this.jobId || String(msg.jobId) !== String(this.jobId)) return;
          this.appendDelta(msg);
          return;
        }

        // subscribe/unsubscribe ack (optional)
        if (msg && msg.action === 'subscribeTranscript' && msg.success && this.jobId && String(msg.jobId) === String(this.jobId)) {
          this.isSubscribed = true;
        }
        if (msg && msg.action === 'unsubscribeTranscript' && msg.success && this.jobId && String(msg.jobId) === String(this.jobId)) {
          this.isSubscribed = false;
        }
      });

    this.trySubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jobId'] && !changes['jobId'].firstChange) {
      // swap subscriptions when job changes
      this.tryUnsubscribe(changes['jobId'].previousValue);
      this.transcript = [];
      this.lastSeqSeen = null;
      this.isSubscribed = false;
      this.trySubscribe();
    }
  }

  ngOnDestroy(): void {
    this.tryUnsubscribe(this.jobId);
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  clear(): void {
    this.transcript = [];
    this.lastSeqSeen = null;
    this.isAutoScrollPaused = false;
    this.scrollToBottomSoon();
  }

  onTranscriptScroll(): void {
    if (!this.scrollContainer || !this.scrollContainer.nativeElement) return;
    const el = this.scrollContainer.nativeElement;
    const distanceFromBottom = (el.scrollHeight - el.scrollTop - el.clientHeight);
    // If user scrolls up, pause auto-scroll; resume when they come back near the bottom.
    this.isAutoScrollPaused = distanceFromBottom > 120;
  }

  private trySubscribe(): void {
    if (!this.jobId) return;
    this.wsService.ensureConnected(API_WEB_SOCKET);
    this.wsService.send({
      action: 'subscribeTranscript',
      data: { jobId: this.jobId }
    });
    this.isSubscribed = true; // optimistic until ack
  }

  private tryUnsubscribe(jobId: string | null): void {
    if (!jobId) return;
    this.wsService.send({
      action: 'unsubscribeTranscript',
      data: { jobId }
    });
  }

  private appendDelta(delta: any): void {
    const seq = typeof delta.seq === 'number' ? delta.seq : null;
    if (seq !== null) {
      // best-effort de-dupe
      if (this.lastSeqSeen !== null && seq <= this.lastSeqSeen) return;
      this.lastSeqSeen = seq;
    }

    const nextRow: {
      jobId: string;
      role: 'user' | 'assistant' | string;
      text: string;
      ts?: number;
      is_final?: boolean;
      seq?: number;
    } = {
      jobId: String(delta.jobId),
      role: delta.role || 'user',
      text: String(delta.text || ''),
      ts: typeof delta.ts === 'number' ? delta.ts : undefined,
      is_final: !!delta.is_final
    };

    if (seq !== null) {
      nextRow.seq = seq;
    }

    this.transcript = [
      ...this.transcript,
      nextRow
    ];

    if (!this.isAutoScrollPaused) {
      this.scrollToBottomSoon();
    }
  }

  private scrollToBottomSoon(): void {
    // Wait for DOM to render the new row before scrolling.
    setTimeout(() => {
      try {
        if (!this.scrollContainer || !this.scrollContainer.nativeElement) return;
        const el = this.scrollContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      } catch { }
    }, 0);
  }
}
